import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Check, Info } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';

type Plan = 'free' | 'starter' | 'pro';

const emailSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
});

const Tariffs = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentPlan } = useSubscription();
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);

  const plans = [
    {
      id: 'free' as Plan,
      name: t('pricing.free'),
      price: '$0',
      features: [
        t('pricing.features.free.property'),
        t('pricing.features.free.unit'),
        t('pricing.features.free.user'),
        t('pricing.features.free.storage'),
        t('pricing.features.free.transactions'),
        t('pricing.features.free.exports'),
      ],
    },
    {
      id: 'starter' as Plan,
      name: t('pricing.starter'),
      price: '$9-12',
      popular: true,
      features: [
        t('pricing.features.starter.properties'),
        t('pricing.features.starter.units'),
        t('pricing.features.starter.assistant'),
        t('pricing.features.starter.storage'),
        t('pricing.features.starter.transactions'),
        t('pricing.features.starter.calendar'),
        t('pricing.features.starter.tasks'),
        t('pricing.features.starter.currency'),
      ],
    },
    {
      id: 'pro' as Plan,
      name: t('pricing.pro'),
      price: '$19-25',
      features: [
        t('pricing.features.pro.properties'),
        t('pricing.features.pro.units'),
        t('pricing.features.pro.assistants'),
        t('pricing.features.pro.storage'),
        t('pricing.features.pro.transactions'),
        t('pricing.features.pro.analytics'),
        t('pricing.features.pro.advertising'),
        t('pricing.features.pro.pdf'),
      ],
    },
  ];

  const handlePlanSelect = async (plan: Plan) => {
    // If user is authorized - update subscription directly
    if (user) {
      if (plan === currentPlan) {
        toast.info(t('pricing.freePlanActive'));
        return;
      }

      try {
        setLoading(true);
        
        const { error } = await supabase
          .from('subscriptions')
          .update({
            plan: plan,
            status: 'active',
            trial_ends_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            current_period_start: new Date().toISOString(),
            current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id);
        
        if (error) throw error;
        
        setShowMaintenance(true);
        toast.success(t('pricing.planUpdated'));
        
        // Reload subscription data
        queryClient.invalidateQueries({ queryKey: ['subscription'] });
      } catch (error: any) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    } else {
      // If not authorized - show email form
      if (plan === 'free') {
        toast.info(t('pricing.freePlanActive'));
        return;
      }
      setSelectedPlan(plan);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const validation = emailSchema.safeParse({ email });
      if (!validation.success) {
        toast.error(validation.error.errors[0].message);
        return;
      }

      // Save to pricing_leads table
      const { error: leadError } = await supabase
        .from('pricing_leads')
        .insert({
          email,
          plan: selectedPlan,
          source: 'tariffs_page',
        });

      if (leadError) throw leadError;

      // Show maintenance banner
      setShowMaintenance(true);
      toast.success('Email saved successfully!');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <Helmet>
        <link rel="canonical" href="https://rental.shift-box.com/tariffs" />
        <link rel="alternate" hrefLang="x-default" href="https://rental.shift-box.com/tariffs" />
      </Helmet>
      <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">{t('pricing.title')}</h1>
        <p className="text-lg text-muted-foreground">
          {t('pricing.selectPlanSubtitle')}
        </p>
      </div>

      {showMaintenance && (
        <Alert className="mb-8">
          <Info className="h-4 w-4" />
          <AlertDescription className="text-base">
            {t('pricing.maintenanceBanner')}
          </AlertDescription>
        </Alert>
      )}

      {selectedPlan && !showMaintenance && (
        <Card className="mb-8 border-primary">
          <CardHeader>
            <CardTitle>{t('pricing.emailCapture')}</CardTitle>
            <CardDescription>
              {t('pricing.selectedPlan')} <span className="font-semibold">{plans.find(p => p.id === selectedPlan)?.name}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.enterEmail')}
                  required
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={loading}>
                  {loading ? t('common.loading') : t('pricing.startFreeTrial')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSelectedPlan(null)}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-8 md:grid-cols-3">
        {plans.map((plan) => (
          <Card
            key={plan.id}
            className={`relative ${plan.popular ? 'border-primary shadow-lg' : ''}`}
          >
            {plan.popular && (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                {t('pricing.popular')}
              </Badge>
            )}
            {user && plan.id === currentPlan && (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2" variant="secondary">
                {t('pricing.currentActivePlan')}
              </Badge>
            )}
            <CardHeader>
              <CardTitle className="text-2xl">{plan.name}</CardTitle>
              <CardDescription>
                <span className="text-3xl font-bold text-foreground">
                  {plan.price}
                </span>
                {plan.id !== 'free' && <span className="text-sm">{t('pricing.perMonth')}</span>}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button
                className="w-full"
                variant={plan.popular ? 'default' : 'outline'}
                onClick={() => handlePlanSelect(plan.id)}
                disabled={loading || (user && plan.id === currentPlan)}
              >
                {user && plan.id === currentPlan 
                  ? t('pricing.currentPlan')
                  : user 
                    ? t('pricing.changePlan')
                    : t('pricing.selectPlan')
                }
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>

      <div className="mt-12 text-center text-sm text-muted-foreground">
        <p>{t('pricing.allPlansInclude')}</p>
        <p className="mt-2">{t('pricing.pricesMayVary')}</p>
      </div>
      </div>
    </AppLayout>
  );
};

export default Tariffs;
